import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Phone, Clock, Shield, RotateCcw, CheckCircle } from 'lucide-react'
import { settingsService } from '../services/api'

const ReturnPolicy = () => {
  const { data: settings } = useQuery({
    queryKey: ['storeSettings'],
    queryFn: settingsService.getSettings,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-6">
            <RotateCcw className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            سياسة الإسترجاع
          </h1>
          <p className="text-xl text-gray-600">
            نخبة المسابيح - نقدم لكم أجود أنواع المسابيح والسبح بأفضل الأسعار. جودة عالية وخدمة متميزة.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* 14 Days Return Policy */}
          <div className="bg-primary-600 text-white px-8 py-6">
            <div className="flex items-center">
              <Clock className="w-8 h-8 ml-4" />
              <h2 className="text-2xl font-bold">سياسة الإسترجاع لمدة ١٤ يوم</h2>
            </div>
          </div>

          <div className="px-8 py-8">
            {/* Introduction */}
            <div className="mb-8">
              <p className="text-lg text-gray-700 leading-relaxed">
                هل غيرت رأيك؟ فهذا أمر جيد تماماً. حيث يمكنك إعادة مشترواتك خلال 
                <span className="font-bold text-primary-600"> ١٤ يوم </span>
                من تاريخ الشراء.
              </p>
            </div>

            {/* Easy and Free Returns */}
            <div className="mb-8">
              <div className="flex items-start mb-4">
                <CheckCircle className="w-6 h-6 text-green-500 ml-3 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">استرجاع سهل ومجاني</h3>
                  <p className="text-gray-700 leading-relaxed">
                    سوف نهتم بعملية الاسترجاع بالكامل حتى استرداد جميع أموالكم. 
                    وبكل تأكيد، فالتكلفة مجانية تماماً.
                  </p>
                </div>
              </div>
            </div>

            {/* Return Methods */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                ٢ طرق سهلة لإسترجاع المنتج
              </h3>

              {/* Phone Method */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="flex items-start">
                  <div className="bg-primary-100 rounded-full p-3 ml-4">
                    <Phone className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      ٢. عبر الهاتف
                    </h4>
                    <p className="text-gray-700 mb-3">
                      اتصل على 
                      {settings?.phone_number && (
                        <a 
                          href={`tel:${settings.phone_number}`}
                          className="font-bold text-primary-600 hover:text-primary-700 transition-colors mx-2"
                        >
                          {settings.phone_number}
                        </a>
                      )}
                      لتسجيل طلب الاسترجاع وتحديد موعد الاستلام.
                    </p>
                    {settings?.phone_number && (
                      <a 
                        href={`tel:${settings.phone_number}`}
                        className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <Phone className="w-4 h-4 ml-2" />
                        اتصل الآن
                      </a>
                    )}
                  </div>
                </div>
              </div>

              {/* WhatsApp Method (if available) */}
              {settings?.whatsapp_number && (
                <div className="bg-green-50 rounded-xl p-6">
                  <div className="flex items-start">
                    <div className="bg-green-100 rounded-full p-3 ml-4">
                      <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.486"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-900 mb-2">
                        ١. عبر واتساب
                      </h4>
                      <p className="text-gray-700 mb-3">
                        راسلنا على واتساب لطلب الإسترجاع بطريقة سريعة ومريحة.
                      </p>
                      <a 
                        href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.486"/>
                        </svg>
                        راسلنا واتساب
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Important Notes */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-start">
                <Shield className="w-6 h-6 text-blue-600 ml-3 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="text-lg font-bold text-blue-900 mb-2">ملاحظات مهمة</h4>
                  <ul className="text-blue-800 space-y-2">
                    <li>• يجب أن تكون المنتجات في حالتها الأصلية</li>
                    <li>• الإسترجاع متاح خلال ١٤ يوم من تاريخ الشراء</li>
                    <li>• جميع عمليات الإسترجاع مجانية تماماً</li>
                    <li>• سيتم استرداد المبلغ كاملاً</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-12 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">هل تحتاج مساعدة؟</h3>
          <p className="text-gray-600 mb-6">فريق خدمة العملاء جاهز لمساعدتك</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {settings?.phone_number && (
              <a 
                href={`tel:${settings.phone_number}`}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Phone className="w-5 h-5 ml-2" />
                {settings.phone_number}
              </a>
            )}
            {settings?.email && (
              <a 
                href={`mailto:${settings.email}`}
                className="inline-flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                {settings.email}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReturnPolicy
